import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency, CURRENCIES } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Menu, X, LayoutDashboard, User, LogOut, Phone } from "lucide-react";

const NAV_LINK = "nav-link-elegant text-[12.5px] font-ui font-medium tracking-[0.16em] uppercase whitespace-nowrap text-foreground/70 hover:text-foreground transition-colors duration-200 py-1";

export default function Navbar() {
  const { user, logout } = useAuth();
  useEffect(() => {
    if (!window.__edVisitTracked) {
      window.__edVisitTracked = true;
      let sid = sessionStorage.getItem("ed_sid");
      if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem("ed_sid", sid);
        localStorage.setItem("ed_session_count", String(Number(localStorage.getItem("ed_session_count") || "0") + 1));
      }
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: window.location.pathname, referrer: document.referrer || "", session_id: sid }),
      }).catch(() => {});
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo-meta`)
      .then((r) => r.json())
      .then((tags) => {
        Object.entries(tags || {}).forEach(([name, content]) => {
          if (!document.querySelector(`meta[name="${name}"]`)) {
            const m = document.createElement("meta");
            m.setAttribute("name", name);
            m.setAttribute("content", content);
            document.head.appendChild(m);
          }
        });
      })
      .catch(() => {});
  }, []);
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));
  const linkCls = (to) => `${NAV_LINK}${isActive(to) ? " nav-active" : ""}`;

  const links = (
    <>
      <Link data-testid="nav-home-link" to="/" onClick={() => setOpen(false)} className={linkCls("/")}>
        Home
      </Link>
      <Link data-testid="nav-fleet-link" to="/fleet" onClick={() => setOpen(false)} className={linkCls("/fleet")}>
        Fleet
      </Link>
      <Link data-testid="nav-about-link" to="/about" onClick={() => setOpen(false)} className={linkCls("/about")}>
        About
      </Link>
      <Link data-testid="nav-checkin-link" to="/check-in" onClick={() => setOpen(false)} className={linkCls("/check-in")}>
        Check-In
      </Link>
      <Link data-testid="nav-loyalty-link" to="/loyalty" onClick={() => setOpen(false)} className={linkCls("/loyalty")}>
        Loyalty Program
      </Link>
      <Link data-testid="nav-contact-link" to="/contact" onClick={() => setOpen(false)} className={linkCls("/contact")}>
        Contact Us
      </Link>
      <Link data-testid="nav-policies-link" to="/policies" onClick={() => setOpen(false)} className={linkCls("/policies")}>
        Policies
      </Link>
      <a data-testid="nav-phone-link" href="tel:+12428131775" onClick={() => setOpen(false)} className="relative group/phone flex items-center text-primary hover:text-primary/80 transition-colors duration-200" aria-label="Call Easy Drive Car Rental at (242) 813-1775">
        <Phone size={15} />
        <span data-testid="nav-phone-tooltip" className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-3.5 whitespace-nowrap rounded-full bg-[#1A1A1A] px-6 py-3 text-sm font-ui font-medium tracking-[0.18em] text-white opacity-0 shadow-[0_10px_32px_rgba(199,30,46,0.5),0_0_0_1px_rgba(199,30,46,0.45),0_0_22px_rgba(199,30,46,0.35)] transition-[opacity,transform] duration-300 -translate-y-1 group-hover/phone:opacity-100 group-hover/phone:translate-y-0">
          (242) 813-1775
        </span>
      </a>
      {user && user.role !== "admin" && (
        <Link data-testid="nav-account-link" to="/account" onClick={() => setOpen(false)} className="text-sm font-ui tracking-wide text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-1.5">
          <User size={14} /> My Bookings
        </Link>
      )}
      {user && user.role === "admin" && (
        <Link data-testid="nav-admin-link" to="/admin" onClick={() => setOpen(false)} className="text-sm font-ui tracking-wide text-primary hover:text-primary/80 transition-colors duration-200 flex items-center gap-1.5">
          <LayoutDashboard size={14} /> Admin
        </Link>
      )}
      <Link data-testid="nav-book-now-link" to="/fleet" onClick={() => setOpen(false)}>
        <Button size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shadow-[0_6px_20px_rgba(199,30,46,0.3)] hover:shadow-[0_8px_26px_rgba(199,30,46,0.45)] hover:-translate-y-0.5 transition-all duration-300">Book Now</Button>
      </Link>
      {user ? (
        <Button data-testid="nav-logout-button" variant="outline" size="sm" onClick={handleLogout} className="rounded-full border-border bg-transparent text-foreground hover:bg-black/5 hover:text-primary">
          <LogOut size={14} className="mr-1.5" /> Sign Out
        </Button>
      ) : (
        <Link data-testid="nav-login-link" to="/login" onClick={() => setOpen(false)}>
          <Button size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shadow-[0_6px_20px_rgba(199,30,46,0.3)] hover:shadow-[0_8px_26px_rgba(199,30,46,0.45)] hover:-translate-y-0.5 transition-all duration-300">
            <User size={14} className="mr-1.5" /> Log In
          </Button>
        </Link>
      )}
      <select
        data-testid="currency-select"
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
        aria-label="Display currency"
        className="bg-transparent border border-border rounded-full px-2.5 py-1 text-xs font-ui cursor-pointer hover:border-primary/50 transition-colors duration-200 outline-none"
      >
        {Object.keys(CURRENCIES).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <LanguageSwitcher />
    </>
  );

  return (
    <header data-testid="site-header" className="no-print fixed top-0 inset-x-0 z-50 glass border-b border-primary/15">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link data-testid="nav-logo" to="/" className="flex items-center gap-2.5 group shrink-0 mr-10">
          <img src="/logo.png" alt="Easy Drive Car Rental" className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]" />
        </Link>
        <nav className="hidden md:flex items-center gap-7 whitespace-nowrap ml-4">{links}</nav>
        <button data-testid="nav-mobile-menu-button" className="md:hidden text-foreground" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <nav data-testid="nav-mobile-menu" className="md:hidden glass border-t border-border px-6 py-4 flex flex-col gap-3 fade-up">
          <div className="flex items-center gap-2.5 pb-3 border-b border-border/60">
            <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="nav-mobile-logo" className="h-10 w-auto object-contain" />
          </div>
          {links}
        </nav>
      )}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70" />
    </header>
  );
}
